import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { html, type TemplateResult } from 'lit';
import { globusVideoPlayer } from '../components/globusVideoPlayer.js';
import type { LandingLocale } from '../../shared/content/landingContent.js';
import type { GlobusProfession } from '../../shared/content/landingContentShared.js';
import {
  globusJobApplicationHref,
  requiresPorkWarning,
  type GlobusJobContent,
} from '../../shared/content/globusJobContent.js';
import type { StaticPageContent } from '../../shared/content/staticPageContent.js';
import { staticPageUiCopy } from '../../shared/content/staticPageLocalization.js';
import {
  editorNodeClass,
  editorNodeClick,
  editorNodePointerDown,
  editorNodeStyle,
  type LandingEditorRenderOptions,
} from '../editor/landingEditorRender.js';
import { editorSemanticText } from '../editor/editorSemanticText.js';

interface GlobusJobSectionsOptions {
  page: StaticPageContent;
  locale: LandingLocale;
  sectionStyle: string;
  editor?: LandingEditorRenderOptions;
}

type GlobusJobFilter = GlobusProfession | 'all';

const globusJobFilterIcons: Record<GlobusProfession, string> = {
  bakery: 'icon-[material-symbols--bakery-dining]',
  butcher: 'icon-[material-symbols--set-meal]',
  'fresh-counter': 'icon-[material-symbols--storefront]',
  gastronomy: 'icon-[material-symbols--restaurant]',
};

const normalizedSearchValue = (value: string, locale: LandingLocale) =>
  value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/gu, '')
    .toLocaleLowerCase(locale)
    .trim();

const searchableJobText = (job: GlobusJobContent) =>
  [
    job.title,
    job.eyebrow,
    job.introduction,
    job.description,
    job.distinction,
    ...job.tasks,
    ...job.requirements,
    ...job.benefits,
    job.warning?.title,
    job.warning?.text,
  ]
    .filter((value): value is string => Boolean(value))
    .join(' ');

const editableText = (
  tag: 'p' | 'h2' | 'h3' | 'span' | 'strong',
  value: string,
  elementId: string,
  className: string,
  editor?: LandingEditorRenderOptions,
): TemplateResult => {
  if (tag === 'h2' || tag === 'h3' || tag === 'p') {
    return editorSemanticText({ editor, elementId, fallbackTag: tag, className, content: value });
  }
  const classes = editorNodeClass(editor, elementId, className);
  const style = editorNodeStyle(editor, elementId);
  const pointerDown = (event: PointerEvent) => editorNodePointerDown(editor, elementId, event);
  const click = (event: MouseEvent) => editorNodeClick(editor, elementId, event);
  if (tag === 'span')
    return html`<span class=${classes} style=${style} @pointerdown=${pointerDown} @click=${click}>${value}</span>`;
  return html`<strong class=${classes} style=${style} @pointerdown=${pointerDown} @click=${click}>${value}</strong>`;
};

const renderList = (
  job: GlobusJobContent,
  jobId: string,
  key: 'tasks' | 'requirements',
  editor?: LandingEditorRenderOptions,
) => {
  const items = job[key];
  return html`
    <div class="vp-globus-job-list-block">
      ${editableText(
        'h3',
        job[key === 'tasks' ? 'tasksHeading' : 'requirementsHeading'],
        `${jobId}.${key}Heading`,
        'vp-globus-job-list-title',
        editor,
      )}
      <ul class="vp-globus-job-list">
        ${items.map((item, index) => {
          const itemId = `${jobId}.${key}.${index}`;
          return html`<li>
            <span class="icon-[material-symbols--check-circle]" aria-hidden="true"></span>
            ${editableText('span', item, itemId, '', editor)}
          </li>`;
        })}
      </ul>
    </div>
  `;
};

const isVideoPlayerInteraction = (event: Event) =>
  (event.target as HTMLElement | null)?.closest('.video-js, .vjs-control, .vjs-menu') !== null;

const videoPointerDown = (editor: LandingEditorRenderOptions | undefined, elementId: string, event: PointerEvent) => {
  if (editor?.mode === 'editor' && isVideoPlayerInteraction(event)) {
    event.stopPropagation();
    return;
  }
  editorNodePointerDown(editor, elementId, event);
};

const videoClick = (editor: LandingEditorRenderOptions | undefined, elementId: string, event: MouseEvent) => {
  if (editor?.mode === 'editor' && isVideoPlayerInteraction(event)) {
    event.stopPropagation();
    return;
  }
  editorNodeClick(editor, elementId, event);
};

const renderJob = (
  job: GlobusJobContent,
  index: number,
  page: StaticPageContent,
  locale: LandingLocale,
  editor?: LandingEditorRenderOptions,
) => {
  const overview = page.globusJobsOverview;
  if (!overview) return '';
  const jobId = `static.globus.globusJobs.${index}`;
  const videoId = `${jobId}.video`;
  return html`
    <article
      id=${`globus-job-${job.profession}`}
      class=${editorNodeClass(editor, jobId, 'vp-globus-job reveal-card')}
      style=${editorNodeStyle(editor, jobId)}
      data-profession=${job.profession}
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, jobId, event)}
      @click=${(event: MouseEvent) => editorNodeClick(editor, jobId, event)}
    >
      <div
        class=${editorNodeClass(editor, videoId, 'vp-globus-job-media')}
        style=${editorNodeStyle(editor, videoId)}
        @pointerdown=${(event: PointerEvent) => videoPointerDown(editor, videoId, event)}
        @click=${(event: MouseEvent) => videoClick(editor, videoId, event)}
      >
        ${globusVideoPlayer({
          id: videoId,
          src: job.video,
          poster: job.poster,
          title: job.videoTitle,
          captionTracks:
            job.captionTracks ??
            (job.captionsSrc
              ? [
                  {
                    src: job.captionsSrc,
                    srclang: locale,
                    label: job.captionsLabel || locale.toUpperCase(),
                  },
                ]
              : []),
          locale,
          unavailableText: overview.videoUnavailable,
          loadingText: overview.videoLoading,
          eager: editor?.mode === 'editor',
        })}
      </div>

      <header class="vp-globus-job-heading">
        ${editableText('p', job.eyebrow, `${jobId}.eyebrow`, 'vp-globus-job-eyebrow', editor)}
        ${editableText('h3', job.title, `${jobId}.title`, 'vp-globus-job-title', editor)}
        ${editableText('p', job.introduction, `${jobId}.introduction`, 'vp-globus-job-lead', editor)}
      </header>

      <div class="vp-globus-job-body">
        ${editableText('p', job.description, `${jobId}.description`, 'vp-globus-job-description', editor)}
        ${job.distinction
          ? html`<div class="vp-globus-job-distinction">
              <span class="icon-[material-symbols--info-outline]" aria-hidden="true"></span>
              ${editableText('p', job.distinction, `${jobId}.distinction`, '', editor)}
            </div>`
          : ''}
        <div class="vp-globus-job-lists">
          ${renderList(job, jobId, 'tasks', editor)} ${renderList(job, jobId, 'requirements', editor)}
        </div>
        <div class="vp-globus-job-benefits" aria-label=${job.benefitsHeading}>
          ${editableText('h3', job.benefitsHeading, `${jobId}.benefitsHeading`, 'vp-globus-job-list-title', editor)}
          <ul>
            ${job.benefits.map(
              (benefit, benefitIndex) =>
                html`<li>
                  <span class="icon-[material-symbols--verified-rounded]" aria-hidden="true"></span>
                  ${editableText('span', benefit, `${jobId}.benefits.${benefitIndex}`, '', editor)}
                </li>`,
            )}
          </ul>
        </div>
        ${job.warning && requiresPorkWarning(job)
          ? html`<aside class="vp-globus-pork-warning" role="note" aria-labelledby=${`${jobId}-warning-title`}>
              <span class="icon-[material-symbols--warning-rounded]" aria-hidden="true"></span>
              <div>
                <strong id=${`${jobId}-warning-title`}
                  >${editableText('span', job.warning.title, `${jobId}.warning.title`, '', editor)}</strong
                >
                ${editableText('p', job.warning.text, `${jobId}.warning.text`, '', editor)}
              </div>
            </aside>`
          : ''}
        <a
          class=${editorNodeClass(editor, `${jobId}.cta.label`, 'vp-action vp-action-primary vp-globus-job-cta')}
          style=${editorNodeStyle(editor, `${jobId}.cta.label`)}
          href=${globusJobApplicationHref(job)}
          aria-label=${job.cta.label}
          @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${jobId}.cta.label`, event)}
          @click=${(event: MouseEvent) => editorNodeClick(editor, `${jobId}.cta.label`, event)}
        >
          ${job.cta.label}
          <span class="icon-[material-symbols--arrow-forward-rounded]" aria-hidden="true"></span>
        </a>
      </div>
    </article>
  `;
};

export const globusJobSections = stateful(({ page, locale, sectionStyle, editor }: GlobusJobSectionsOptions) => {
  const [query, setQuery] = useState('', [locale]);
  const [activeFilter, setActiveFilter] = useState<GlobusJobFilter>('all', [locale]);
  const jobs = page.globusJobs;
  const overview = page.globusJobsOverview;
  if (!jobs?.length || !overview) return '';
  const sectionId = 'static.globus.globusJobs';
  const searchCopy = staticPageUiCopy(locale).globusJobSearch;
  const normalizedQuery = normalizedSearchValue(query, locale);
  const filteredJobs = jobs
    .map((job, index) => ({ job, index }))
    .filter(({ job }) => {
      const matchesProfession = activeFilter === 'all' || job.profession === activeFilter;
      const matchesQuery =
        !normalizedQuery || normalizedSearchValue(searchableJobText(job), locale).includes(normalizedQuery);
      return matchesProfession && matchesQuery;
    });
  const hasActiveSearch = Boolean(normalizedQuery) || activeFilter !== 'all';
  const resetSearch = () => {
    setQuery('');
    setActiveFilter('all');
  };
  const stopSearchInteraction = (event: Event) => event.stopPropagation();
  return html`
    <section
      class=${editorNodeClass(editor, sectionId, 'vp-globus-jobs-section')}
      style=${sectionStyle}
      aria-labelledby="globus-jobs-title"
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, sectionId, event)}
      @click=${(event: MouseEvent) => editorNodeClick(editor, sectionId, event)}
    >
      <div class="vp-shell vp-globus-jobs-discovery">
        <header class="vp-globus-jobs-intro">
          <div id="globus-jobs-title">
            ${editableText('h2', overview.title, `${sectionId}.overview.title`, 'vp-globus-jobs-title', editor)}
          </div>
          ${editableText('p', overview.intro, `${sectionId}.overview.intro`, 'vp-globus-jobs-intro-text', editor)}
        </header>

        <form
          class="vp-globus-job-finder"
          role="search"
          @submit=${(event: SubmitEvent) => event.preventDefault()}
          @pointerdown=${stopSearchInteraction}
          @click=${stopSearchInteraction}
        >
          <label class="vp-globus-job-search-label" for="globus-job-search">${searchCopy.label}</label>
          <div class="vp-globus-job-search-field">
            <span class="icon-[material-symbols--search-rounded]" aria-hidden="true"></span>
            <input
              id="globus-job-search"
              type="search"
              .value=${query}
              placeholder=${searchCopy.placeholder}
              autocomplete="off"
              enterkeyhint="search"
              @input=${(event: InputEvent) => setQuery((event.currentTarget as HTMLInputElement).value)}
            />
            ${query
              ? html`<button
                  class="vp-globus-job-search-clear"
                  type="button"
                  aria-label=${searchCopy.clearSearch}
                  @click=${() => setQuery('')}
                >
                  <span class="icon-[material-symbols--close-rounded]" aria-hidden="true"></span>
                </button>`
              : ''}
          </div>

          <div class="vp-globus-job-filter-heading">${searchCopy.filtersLabel}</div>
          <div class="vp-globus-job-filters" role="group" aria-label=${searchCopy.filtersLabel}>
            <button
              class="vp-globus-job-filter ${activeFilter === 'all' ? 'is-active' : ''}"
              type="button"
              aria-pressed=${activeFilter === 'all'}
              aria-controls="globus-jobs-list"
              @click=${() => setActiveFilter('all')}
            >
              <span class="icon-[material-symbols--apps-rounded]" aria-hidden="true"></span>
              ${searchCopy.allJobs}
            </button>
            ${jobs.map(
              job => html`
                <button
                  class="vp-globus-job-filter ${activeFilter === job.profession ? 'is-active' : ''}"
                  type="button"
                  aria-pressed=${activeFilter === job.profession}
                  aria-controls="globus-jobs-list"
                  @click=${() => setActiveFilter(job.profession)}
                >
                  <span class=${globusJobFilterIcons[job.profession]} aria-hidden="true"></span>
                  ${job.title}
                </button>
              `,
            )}
          </div>
          <div class="vp-globus-job-results" role="status" aria-live="polite" aria-atomic="true">
            ${searchCopy.results(filteredJobs.length, jobs.length)}
          </div>
        </form>
      </div>

      <div id="globus-jobs-list" class="vp-globus-jobs-list" aria-labelledby="globus-jobs-title">
        ${filteredJobs.length
          ? filteredJobs.map(({ job, index }) => renderJob(job, index, page, locale, editor))
          : html`<div class="vp-globus-jobs-empty" role="status">
              <span class="icon-[material-symbols--search-off-rounded]" aria-hidden="true"></span>
              <strong>${searchCopy.emptyTitle}</strong>
              <p>${searchCopy.emptyText}</p>
              ${hasActiveSearch
                ? html`<button
                    type="button"
                    @click=${(event: MouseEvent) => {
                      event.stopPropagation();
                      resetSearch();
                    }}
                  >
                    ${searchCopy.resetFilters}
                  </button>`
                : ''}
            </div>`}
      </div>
      <div class="vp-shell">
        <aside class="vp-globus-shared-benefits">
          <div class="vp-globus-shared-benefits-copy">
            ${editableText(
              'p',
              overview.sharedBenefitsEyebrow,
              `${sectionId}.overview.sharedBenefitsEyebrow`,
              'vp-eyebrow',
              editor,
            )}
            ${editableText('h2', overview.sharedBenefitsTitle, `${sectionId}.overview.sharedBenefitsTitle`, '', editor)}
            ${editableText('p', overview.sharedBenefitsText, `${sectionId}.overview.sharedBenefitsText`, '', editor)}
          </div>
          <ul>
            ${overview.sharedBenefits.map(
              (benefit, index) =>
                html`<li>
                  <span class="icon-[material-symbols--check-circle]" aria-hidden="true"></span>
                  ${editableText('span', benefit, `${sectionId}.overview.sharedBenefits.${index}`, '', editor)}
                </li>`,
            )}
          </ul>
        </aside>
        <aside class="vp-globus-conditions-note">
          <span class="icon-[material-symbols--contract-outline]" aria-hidden="true"></span>
          <div>
            ${editableText('strong', overview.conditionsTitle, `${sectionId}.overview.conditionsTitle`, '', editor)}
            ${editableText('p', overview.conditionsText, `${sectionId}.overview.conditionsText`, '', editor)}
          </div>
        </aside>
      </div>
    </section>
  `;
});
